import {
  type Completion,
  type CompletionContext,
  type CompletionResult,
  type CompletionSource,
} from "@codemirror/autocomplete";
import { type EditorView } from "@codemirror/view";
import { hrefFor, linkLabel } from "../vault/links.ts";

/**
 * Buscador de documentos al tipear `[[`.
 *
 * El gesto es el de Obsidian, pero **lo que se inserta es markdown estándar**:
 * `[Título](carpeta/nota.md)`. Es la decisión de `vault/links.ts` — la
 * ergonomía del wikilink sin su sintaxis, para que el `.md` siga abriéndose
 * igual en cualquier otro editor.
 *
 * La lista sale del árbol que ya tiene la sidebar en memoria, así que abrir el
 * menú no lee el disco.
 */

export interface LinkMenuOptions {
  /** Rutas del vault que se pueden enlazar (ver `linkTargets`). */
  targets: () => string[];
  /** Ruta del documento que se está editando: los destinos se escriben
   *  relativos a su carpeta. */
  docPath: () => string;
}

/** Lo más largo que se muestra: más allá de esto la respuesta es escribir un
 *  par de letras más, no una lista más larga (mismo criterio que ⌘K). */
const MAX_OPTIONS = 30;

/** Minúsculas y sin acentos, para que "cafe" encuentre "Café". */
function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Cuánto merece estar arriba: primero lo que EMPIEZA con lo tipeado, después
 * lo que lo contiene en el nombre, y último lo que sólo coincide en la carpeta.
 * -1 = no coincide.
 */
function score(path: string, query: string): number {
  if (!query) return 1;
  const name = normalize(path.split("/").pop() ?? "");
  if (name.startsWith(query)) return 3;
  if (name.includes(query)) return 2;
  if (normalize(path).includes(query)) return 1;
  return -1;
}

function toCompletion(target: string, docPath: string): Completion {
  const label = linkLabel(target);
  const dir = target.slice(0, target.lastIndexOf("/"));
  return {
    label,
    detail: dir || undefined,
    type: target.endsWith(".canvas") ? "canvas" : "note",
    apply: (view: EditorView, _c: Completion, from: number, to: number) => {
      const insert = `[${label}](${hrefFor(docPath, target)})`;
      view.dispatch({
        changes: { from, to, insert },
        selection: { anchor: from + insert.length },
        scrollIntoView: true,
        userEvent: "input.complete",
      });
    },
  };
}

export function linkSource(opts: LinkMenuOptions): CompletionSource {
  return (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);
    // `[[` seguido de lo tipeado. Un `]` corta el disparador: el enlace ya se
    // cerró y lo que venga después es texto del documento.
    const match = /\[\[([^\[\]]*)$/.exec(before);
    if (!match) return null;

    const query = normalize(match[1]);
    const docPath = opts.docPath();
    const options = opts
      .targets()
      // El documento abierto no se ofrece: un enlace a sí mismo no lleva a
      // ninguna parte y ensucia la lista justo arriba, donde más se mira.
      .filter((t) => t !== docPath)
      .map((target) => ({ target, s: score(target, query) }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => b.s - a.s || a.target.localeCompare(b.target))
      .slice(0, MAX_OPTIONS)
      .map((r) => toCompletion(r.target, docPath));
    if (!options.length) return null;

    return {
      // Desde el primer `[` de `[[`: al elegir, los corchetes se reemplazan
      // por el enlace entero en vez de quedar colgando delante.
      from: context.pos - match[1].length - 2,
      to: context.pos,
      options,
      filter: false, // ya filtramos nosotros (sin acentos, y por ruta)
    };
  };
}
